import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useMerchant } from "@auth";
import { getCustomerGroups, getCustomersCount } from "@api";

const CustomerGroupsContext = createContext();

export const useCustomerGroups = () => {
  const context = useContext(CustomerGroupsContext);

  if (context === undefined) {
    throw new Error(
      "useCustomerGroups must be used within a CustomerGroupsProvider",
    );
  }

  return context;
};

export const CustomerGroupsProvider = ({ children }) => {
  const { merchant } = useMerchant();
  const queryClient = useQueryClient();
  const [groups, setGroups] = useState([]);
  const [customersCount, setCustomersCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!merchant?.id) {
      setGroups([]);
      setCustomersCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [groupsData, countData] = await Promise.all([
        getCustomerGroups(),
        getCustomersCount(),
      ]);
      setGroups(groupsData || []);
      setCustomersCount(countData || 0);
    } catch (error) {
      console.error("Failed to fetch customer data:", error);
      setGroups([]);
      setCustomersCount(0);
    } finally {
      setLoading(false);
    }
  }, [merchant?.id]);

  // Fetch data when merchant changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get a group by ID
  const getGroupById = useCallback(
    (id) => {
      return groups.find((group) => group.id === id);
    },
    [groups],
  );

  // Get a group's name by ID (convenience method)
  const getGroupName = useCallback(
    (id) => {
      return getGroupById(id)?.name;
    },
    [getGroupById],
  );

  // Refresh groups (call this after creating/editing/deleting a group).
  // NB: groups themselves are still managed via local useState. When this
  // context is migrated to useQuery in a follow-up, the explicit invalidate
  // here becomes redundant — delete it then.
  const refreshGroups = useCallback(async () => {
    await fetchData();
    queryClient.invalidateQueries({ queryKey: ["customers"] });
  }, [fetchData, queryClient]);

  const value = {
    groups,
    customersCount,
    loading,
    getGroupById,
    getGroupName,
    refreshGroups,
  };

  return (
    <CustomerGroupsContext.Provider value={value}>
      {children}
    </CustomerGroupsContext.Provider>
  );
};
